<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import {
  fetchVoucherMessages,
  fetchVoucherMessageReadNames,
  deleteVoucher,
  viewAllVouchers,
  viewVouchersNext,
} from "./api";
import { formatDate, toDate } from "@/lib/date";

const router = useRouter();
const tab = ref("vouchers");

// ── Notice History (voucher batches) ────────────────────────────────────────
const messageColumns = [
  { key: "title",   label: "Title",   sortable: true },
  { key: "date",    label: "Date" },
  { key: "actions", label: "Actions", align: "end", width: "210px" },
];
const rawMessages = ref([]);
const {
  rows: messageRows,
  loading: messagesLoading,
  error: messagesError,
  pendingDelete: messagePendingDelete,
  deleting: messageDeleting,
  load: loadMessages,
  askDelete: askDeleteMessage,
  confirmDelete: confirmDeleteMessage,
} = useResourceList({
  fetch: () =>
    fetchVoucherMessages().then((data) => {
      rawMessages.value = Array.isArray(data) ? data : data?.data ?? [];
      return data;
    }),
  remove: deleteVoucher,
  mapRow: (v) => ({
    id: v._id,
    title: v.title,
    date: formatDate(v.startDate ?? v.createdAt),
    _sortDate: toDate(v.updatedAt ?? v.startDate ?? v.createdAt)?.getTime() ?? 0,
  }),
  sort: (a, b) => b._sortDate - a._sortDate,
});

// ── Vouchers (per-employee) ──────────────────────────────────────────────────
const voucherColumns = [
  { key: "batchNo",      label: "Batch No" },
  { key: "employeeName", label: "Employee Name" },
  { key: "employeeId",   label: "Employee Id" },
  { key: "voucherValue", label: "Voucher Value" },
  { key: "serialNo",     label: "BD Voucher Number" },
  { key: "mezVouchId",     label: "Mezzofy Voucher ID", sortable: true },
  { key: "voucherStart", label: "Voucher Start Date" },
  { key: "voucherEnd",   label: "Voucher End Date" },
  { key: "redeem",       label: "Redeemed" },
  { key: "actions",      label: "Actions", align: "end", width: "110px" },
];

const rawVouchers = ref([]);
const voucherRows = ref([]);
const vouchersLoading = ref(false);
const vouchersError = ref("");
const loadingMore = ref(false);
const hasMore = ref(false);
const pageToken = ref(null);

function mapVoucher(v) {
  return {
    id: v._id,
    batchNo: v.batchNo,
    employeeName: v.employeeName,
    employeeId: v.employeeId,
    messageId: v.messageId,
    voucherValue: v.voucherValue,
    serialNo: v.serialNo,
    mezVouchId: v.mezVouchId,
    voucherStart: formatDate(v.voucherStart),
    voucherEnd: formatDate(v.voucherEnd),
    redeem: !!v.redeem,
  };
}

async function loadVouchers() {
  vouchersLoading.value = true;
  vouchersError.value = "";
  try {
    const { voucherData = [], nextPageToken: token = null } = await viewAllVouchers();
    rawVouchers.value = voucherData;
    voucherRows.value = voucherData.map(mapVoucher);
    pageToken.value = token;
    hasMore.value = voucherData.length > 0;
  } catch {
    vouchersError.value = "Couldn't load vouchers.";
  } finally {
    vouchersLoading.value = false;
  }
}

async function loadMoreVouchers() {
  if (loadingMore.value) return;
  loadingMore.value = true;
  try {
    const lastItem = rawVouchers.value[rawVouchers.value.length - 1];
    const token = pageToken.value ?? (lastItem?.createdOn ? new Date(lastItem.createdOn).toISOString() : "");
    const { voucherData = [], nextPageToken: next = null } = await viewVouchersNext(token, lastItem?.serialNo ?? "");
    if (voucherData.length) {
      rawVouchers.value = rawVouchers.value.concat(voucherData);
      voucherRows.value = voucherRows.value.concat(voucherData.map(mapVoucher));
    }
    pageToken.value = next;
    hasMore.value = voucherData.length > 0;
  } catch {
    vouchersError.value = "Couldn't load more vouchers.";
  } finally {
    loadingMore.value = false;
  }
}

loadVouchers();

const voucherPendingDelete = ref(null);
const voucherDeleting = ref(false);
function askDeleteVoucher(row) {
  voucherPendingDelete.value = row;
}
async function confirmDeleteVoucher() {
  if (!voucherPendingDelete.value) return;
  voucherDeleting.value = true;
  try {
    await deleteVoucher(voucherPendingDelete.value.id);
    voucherRows.value = voucherRows.value.filter((r) => r.id !== voucherPendingDelete.value.id);
    rawVouchers.value = rawVouchers.value.filter((r) => r._id !== voucherPendingDelete.value.id);
    voucherPendingDelete.value = null;
  } catch {
    vouchersError.value = "Couldn't delete this voucher. Please try again.";
  } finally {
    voucherDeleting.value = false;
  }
}

// ── Analytics dialog (shared by both tabs) ──────────────────────────────────
const analyticsDialog = ref(false);
const analyticsLoading = ref(false);
const analyticsVoucher = ref(null); // set only when opened from the Vouchers tab
const analyticsBatch = ref(null);
const analyticsReadList = ref([]);

function countReads(batch) {
  if (!batch?.msgContent) return 0;
  return Object.values(batch.msgContent).filter((v) => String(v).includes("read")).length;
}

async function loadBatchAnalytics(batchId) {
  analyticsBatch.value = rawMessages.value.find((m) => m._id === batchId) ?? null;
  if (!analyticsBatch.value) return;
  const names = await fetchVoucherMessageReadNames(batchId).catch(() => []);
  analyticsReadList.value = Array.isArray(names) ? names : [];
}

async function openVoucherAnalytics(row) {
  analyticsDialog.value = true;
  analyticsLoading.value = true;
  analyticsVoucher.value = row;
  analyticsBatch.value = null;
  analyticsReadList.value = [];
  try {
    if (row.messageId) await loadBatchAnalytics(row.messageId);
  } finally {
    analyticsLoading.value = false;
  }
}

async function openBatchAnalytics(row) {
  analyticsDialog.value = true;
  analyticsLoading.value = true;
  analyticsVoucher.value = null;
  analyticsBatch.value = null;
  analyticsReadList.value = [];
  try {
    await loadBatchAnalytics(row.id);
  } finally {
    analyticsLoading.value = false;
  }
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Birthday / LSA Vouchers</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="loadVouchers(); loadMessages()">Refresh</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'bd-vouchers-upload' })">Upload Vouchers</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'bd-vouchers-export' })">Export</VBtn>
      </div>
    </div>

    <VTabs v-model="tab" class="mb-4">
      <VTab value="vouchers">Vouchers</VTab>
      <VTab value="history">Notice History</VTab>
    </VTabs>

    <VAlert v-if="tab === 'vouchers' && vouchersError" type="error" closable class="mb-4" @click:close="vouchersError = ''">{{ vouchersError }}</VAlert>
    <VAlert v-if="tab === 'history' && messagesError" type="error" closable class="mb-4" @click:close="messagesError = ''">{{ messagesError }}</VAlert>

    <template v-if="tab === 'vouchers'">
      <AppDataTable :columns="voucherColumns" :rows="voucherRows" :loading="vouchersLoading" row-key="id" searchable search-placeholder="Search vouchers…" empty-text="No vouchers yet.">
        <template #cell-redeem="{ row }">
          <VChip size="small" :color="row.redeem ? 'success' : 'default'" variant="tonal">{{ row.redeem ? "Redeemed" : "Not Redeemed" }}</VChip>
        </template>
        <template #cell-actions="{ row }">
          <VBtn icon="mdi:chart-bar" size="small" variant="text" title="Analytics" @click.stop="openVoucherAnalytics(row)" />
          <VBtn icon="mdi:delete-outline" size="small" variant="text" color="error" title="Delete" @click.stop="askDeleteVoucher(row)" />
        </template>
      </AppDataTable>
      <div v-if="hasMore" class="d-flex justify-center mt-4">
        <VBtn variant="tonal" :loading="loadingMore" @click="loadMoreVouchers">Load More</VBtn>
      </div>
    </template>

    <AppDataTable v-else :columns="messageColumns" :rows="messageRows" :loading="messagesLoading" row-key="id" searchable search-placeholder="Search…" empty-text="No voucher messages yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="openBatchAnalytics(row)">Analytics</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDeleteMessage(row)">Delete</VBtn>
      </template>
    </AppDataTable>

    <ConfirmDialog :model-value="voucherPendingDelete !== null" title="Delete voucher?" message="Permanently delete this voucher entry?" :loading="voucherDeleting" @update:model-value="voucherPendingDelete = null" @confirm="confirmDeleteVoucher" />
    <ConfirmDialog :model-value="messagePendingDelete !== null" title="Delete voucher batch?" message="Permanently delete this voucher batch entry?" :loading="messageDeleting" @update:model-value="messagePendingDelete = null" @confirm="confirmDeleteMessage" />

    <VDialog v-model="analyticsDialog" max-width="600" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">Analytics — {{ analyticsBatch?.title ?? analyticsVoucher?.batchNo ?? "" }}</VCardTitle>
        <VCardText>
          <div v-if="analyticsLoading" class="d-flex justify-center pa-4"><VProgressCircular indeterminate /></div>
          <template v-else>
            <VList v-if="analyticsVoucher" density="compact" lines="one" class="mb-2">
              <VListItem title="Batch No" :subtitle="analyticsVoucher.batchNo ?? '—'" />
              <VListItem title="Employee Name" :subtitle="analyticsVoucher.employeeName ?? '—'" />
              <VListItem title="Employee Id" :subtitle="analyticsVoucher.employeeId ?? '—'" />
              <VListItem title="Voucher Value" :subtitle="String(analyticsVoucher.voucherValue ?? '—')" />
              <VListItem title="BD Voucher Number" :subtitle="analyticsVoucher.serialNo ?? '—'" />
              <VListItem title="Voucher Start Date" :subtitle="analyticsVoucher.voucherStart ?? '—'" />
              <VListItem title="Voucher End Date" :subtitle="analyticsVoucher.voucherEnd ?? '—'" />
              <VListItem title="Redeemed" :subtitle="analyticsVoucher.redeem ? 'Yes' : 'No'" />
            </VList>
            <VDivider v-if="analyticsVoucher && analyticsBatch" class="mb-2" />
            <div v-if="analyticsBatch">
              <VList density="compact" lines="one">
                <VListItem title="Unique users (Read)" :subtitle="String(countReads(analyticsBatch))" />
                <VListItem title="No. of users included" :subtitle="String(analyticsBatch.userGuids?.length ?? '—')" />
                <VListItem title="Notification Title" :subtitle="analyticsBatch.notif_title ?? '—'" />
                <VListItem title="Topic" :subtitle="Array.isArray(analyticsBatch.topic) ? analyticsBatch.topic.join(', ') : analyticsBatch.topic ?? '—'" />
              </VList>
              <div v-if="analyticsReadList.length" class="mt-3">
                <div class="text-subtitle-2 mb-1">Read by:</div>
                <VTable density="compact">
                  <thead><tr><th>Name</th><th>Company</th><th>Department</th></tr></thead>
                  <tbody>
                    <tr v-for="(u, i) in analyticsReadList" :key="i">
                      <td>{{ u.name ?? u }}</td><td>{{ u.company ?? "—" }}</td><td>{{ u.department ?? "—" }}</td>
                    </tr>
                  </tbody>
                </VTable>
              </div>
              <div v-else class="text-disabled mt-3">No read data available.</div>
            </div>
            <div v-else-if="!analyticsVoucher" class="text-disabled pa-4 text-center">Couldn't load analytics.</div>
          </template>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="analyticsDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
